import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Snackbar
} from '@mui/material';

const ProfileComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const secretToken = searchParams.get('token');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: '',
    bio: '',
    profilePic: 'https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/generic_avatar.png'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Handle file upload - you'll need to implement image upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          profilePic: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/users/profile-complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          secretToken
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update user data in localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          user.firstName = formData.firstName;
          user.lastName = formData.lastName;
          user.photo = formData.profilePic;
          localStorage.setItem('user', JSON.stringify(user));
        }

        // Show success snackbar
        setShowSuccess(true);
        
        // Navigate to hub after 2 seconds
        setTimeout(() => {
          window.location.href = '/hub';
        }, 2000);
      } else {
        setError(data.message || 'Profile update failed');
        setLoading(false);
      }
    } catch (err) {
      setError('An error occurred');
      console.error(err);
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setShowSuccess(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Complete Your Profile
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar
              src={formData.profilePic}
              sx={{ width: 100, height: 100 }}
            />
          </Box>

          <form onSubmit={handleSubmit}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 2 }}
            >
              Upload Profile Picture
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileUpload}
              />
            </Button>

            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              margin="normal"
            />

            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              margin="normal"
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                label="Role"
              >
                <MenuItem value="Artist">Artist</MenuItem>
                <MenuItem value="Performer">Performer</MenuItem>
                <MenuItem value="Mediamaker">Mediamaker</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              multiline
              rows={4}
              margin="normal"
              placeholder="Tell us about yourself..."
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? 'Saving...' : 'Complete Profile'}
            </Button>
          </form>
        </Paper>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          sx={{ width: '100%' }}
          variant="filled"
        >
          Profile completed successfully! Redirecting to Hub...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileComplete;